// What Resources and files reads and writes, as the staff member, with their own session
// (docs/data-model.md 4.5, 10.4.1 and 10.4.2). Row level security decides: owner and admin read and
// write every resource table; the versions and the opens are theirs only.
//
// Reads: 1 product's sections, placements (removed ones too, for Put it back), items, their bodies and
// file versions, the product's weeks, and hub.resource_open_counts; and every live placement in the
// hub, for the chips' counts and "Also in".
//
// Writes, each a direct write under row level security (10.4.2's "catalogue and content edits"), except
// a file, which goes through sign-upload (start, the upload to its signed link, finish), so the link
// stays the same and the old version is kept:
// - sections: add, rename, move, delete (refused while anything that isn't removed is in it)
// - items: add (a file, a tool, a link, text or a video), edit, move, remove (removed_at), put back
// - "Also show it in": a placement in another product, or the one removed there put back
import { AdminError } from '../api.js'
import { fileType, nextOrder, sortByOrder, releaseColumns, releaseUnchanged, linkFrom } from './model.js'
import { vimeoLinkFrom } from '../../products/mentorship/model.js'

export const SECTION_COLUMNS = 'id, product_id, name, sort_order, tab_note'
export const PLACEMENT_COLUMNS = 'id, item_id, product_id, section_id, module_id, recording_id, sort_order, title_override, subline_override, release_kind, release_module_id, release_session_id, release_at, yours_for_life, new_until, removed_at, removed_by, created_at'
export const LIVE_COLUMNS = 'id, item_id, product_id, section_id'
export const ITEM_COLUMNS = 'id, kind, title, subline, home_product_id, current_version_id'
export const BODY_COLUMNS = 'item_id, link_url, text_body, vimeo_url'
export const VERSION_COLUMNS = 'id, item_id, file_name, uploaded_at, superseded_at'
export const MODULE_COLUMNS = 'id, product_id, number, label, title, sort_order'
export const SESSION_COLUMNS = 'id, product_id, module_id, cohort_id, number, title, starts_at'

// Every live placement in the hub: the chips' counts and the other products an item is in
export function loadLive(api) {
  return api.run(api.from('resource_placements').select(LIVE_COLUMNS).is('removed_at', null).order('id'))
}

// 1 product's library. counts is null when hub.resource_open_counts didn't answer, so the screen
// leaves the opens lines out rather than showing wrong ones.
export async function loadProduct(api, productId) {
  const [sections, placements, modules] = await Promise.all([
    api.run(api.from('resource_sections').select(SECTION_COLUMNS).eq('product_id', productId).order('sort_order').order('id')),
    api.run(api.from('resource_placements').select(PLACEMENT_COLUMNS).eq('product_id', productId).order('sort_order').order('id')),
    api.run(api.from('modules').select(MODULE_COLUMNS).eq('product_id', productId).order('sort_order').order('id')),
  ])
  const itemIds = [...new Set(placements.map(pl => pl.item_id))].sort()
  const sessionIds = [...new Set(placements.map(pl => pl.release_session_id).filter(Boolean))].sort()
  const [items, bodies, versions, sessions, counts] = await Promise.all([
    itemIds.length ? api.run(api.from('resource_items').select(ITEM_COLUMNS).in('id', itemIds).order('id')) : [],
    itemIds.length ? api.run(api.from('resource_item_bodies').select(BODY_COLUMNS).in('item_id', itemIds).order('item_id')) : [],
    itemIds.length ? api.run(api.from('resource_item_versions').select(VERSION_COLUMNS).in('item_id', itemIds).order('uploaded_at').order('id')) : [],
    sessionIds.length ? api.run(api.from('sessions').select(SESSION_COLUMNS).in('id', sessionIds).order('id')) : [],
    api.resource_open_counts({ product: productId }).catch(() => null),
  ])
  return { sections, placements, modules, items, bodies, versions, sessions, counts }
}

// An update or delete that must change a row: row level security answers a refused 1 with no rows
async function changed(api, query) {
  const rows = await api.run(query)
  if (!rows || rows.length === 0) throw new AdminError('not_there', 0, api.WORDS.notThere)
  return rows
}

// A section name someone else already has in this product: the line to show
function sectionRefusal(e, name) {
  if (e?.code === '23505') return new AdminError('23505', 409, `There's already a section called ${name}.`)
  return e
}

// ---- Sections ------------------------------------------------------------------------------------

export async function addSection(api, { productId, name, sections }) {
  try {
    return (await api.run(api.from('resource_sections')
      .insert({ product_id: productId, name, sort_order: nextOrder(sections) }).select(SECTION_COLUMNS)))[0]
  } catch (e) { throw sectionRefusal(e, name) }
}

export async function renameSection(api, { id, name }) {
  try {
    return (await changed(api, api.from('resource_sections').update({ name }).eq('id', id).select(SECTION_COLUMNS)))[0]
  } catch (e) { throw sectionRefusal(e, name) }
}

// Deleting an empty section also deletes what was removed from it (the database's
// clear_removed_placements), which can't then be put back
export function deleteSection(api, id) {
  return changed(api, api.from('resource_sections').delete().eq('id', id).select('id'))
}

// sort_order writes (model.js's reorderWrites), 1 row at a time
export async function writeOrder(api, table, writes) {
  for (const w of writes) {
    await changed(api, api.from(table).update({ sort_order: w.sort_order }).eq('id', w.id).select('id'))
  }
}

// ---- Removing and putting back --------------------------------------------------------------------

export function removePlacement(api, { id, actor, at }) {
  return changed(api, api.from('resource_placements').update({ removed_at: at, removed_by: actor })
    .eq('id', id).is('removed_at', null).select('id'))
}

// Put back: the database refuses it (23505) when the same item has been put in this product again since
export async function putBack(api, { id, title, productName }) {
  try {
    return await changed(api, api.from('resource_placements').update({ removed_at: null, removed_by: null })
      .eq('id', id).select('id'))
  } catch (e) {
    if (e?.code === '23505') throw new AdminError('23505', 409, `${title} is in ${productName} again already, so there's nothing to put back.`)
    throw e
  }
}

// ---- Files ---------------------------------------------------------------------------------------

// A file into the resources bucket through sign-upload: start (item_id replaces an item's file,
// product_id makes a new item, a file or, with item_kind 'tool', an online tool), the upload to its
// signed link, then finish
async function upload(api, target, file, finish) {
  const type = fileType(file)
  const start = await api.sign_upload({ action: 'start', kind: 'resource', ...target, file_name: file.name, content_type: type, size_bytes: file.size })
  // the type goes with the file itself (a file is sent as a form part), so a file the browser gave no
  // type is sent with the 1 its name says, which the finish checks again
  const body = file.type === type ? file : new File([file], file.name, { type })
  let res
  try {
    res = await api.db.storage.from(start.bucket).uploadToSignedUrl(start.path, start.token, body, { contentType: type })
  } catch {
    res = { error: true }
  }
  if (res?.error) throw new AdminError('not_uploaded', 0, "The file didn't arrive. Add it again.")
  return api.sign_upload({ action: 'finish', receipt: start.receipt, ...finish })
}

// ---- Items ---------------------------------------------------------------------------------------

const newId = () => crypto.randomUUID()
const clean = s => String(s || '').trim()

// The body a link, text or video item keeps (resource_item_bodies)
function bodyOf(draft) {
  if (draft.kind === 'link') return { link_url: linkFrom(draft.link), text_body: null, vimeo_url: null }
  if (draft.kind === 'video') return { link_url: null, text_body: null, vimeo_url: vimeoLinkFrom(draft.link) }
  return { link_url: null, text_body: draft.text, vimeo_url: null }
}

// The placement's own columns from the draft
function placementColumns(draft, { toIso, nowIso, newUntil, before }) {
  const cols = {
    section_id: draft.sectionId,
    yours_for_life: Boolean(draft.life),
    ...releaseColumns(draft, toIso),
  }
  // Mark it new: 14 days from now when it's ticked now; a mark still running is left as it is
  const wasNew = before?.new_until && Date.parse(before.new_until) > Date.parse(nowIso)
  if (draft.isNew && !wasNew) cols.new_until = newUntil
  if (!draft.isNew && before?.new_until) cols.new_until = null
  return cols
}

// A new item in this product's section: the item (a file, or an online tool's HTML, through
// sign-upload, which makes the item with its first version; a link, text or video made here with its
// body), then its placement and any "Also show it in"
export async function addItem(api, { product, draft, actor, sectionItems, others, toIso, nowIso, newUntil }) {
  const title = clean(draft.title)
  const subline = clean(draft.subline) || null
  let itemId
  if (draft.kind === 'file' || draft.kind === 'tool') {
    const target = draft.kind === 'tool' ? { product_id: product.id, item_kind: 'tool' } : { product_id: product.id }
    const done = await upload(api, target, draft.file, { title })
    itemId = done.item_id
    if (subline) await changed(api, api.from('resource_items').update({ subline }).eq('id', itemId).select('id'))
  } else {
    itemId = newId()
    await api.run(api.from('resource_items').insert({
      id: itemId, kind: draft.kind, title, subline, home_product_id: product.id, created_by: actor,
    }).select('id'))
    await api.run(api.from('resource_item_bodies').insert({ item_id: itemId, ...bodyOf(draft) }).select('item_id'))
  }
  const placement = (await api.run(api.from('resource_placements').insert({
    item_id: itemId, product_id: product.id, sort_order: nextOrder(sectionItems), created_by: actor,
    ...placementColumns(draft, { toIso, nowIso, newUntil }),
  }).select(PLACEMENT_COLUMNS)))[0]
  await showAlsoIn(api, { itemId, sectionName: others.sectionName, add: draft.also, remove: [], actor, at: nowIso })
  return placement
}

// An existing item: a new version of its file, its title and line (the item's own when it's in this
// product only; this product's own words when it's in others too, so they keep theirs), its body,
// its placement, and the products it's also shown in
export async function saveItem(api, { product, draft, pl, item, body, alsoBefore, elsewhere, actor, sectionItems, others, toIso, nowIso, newUntil }) {
  const title = clean(draft.title)
  const subline = clean(draft.subline) || null
  // a new version of the file; nobody is emailed about it (stage 6: sign-upload takes no email_holders)
  if (draft.file) await upload(api, { item_id: item.id }, draft.file, {})

  const patch = {}
  if (elsewhere) {
    const ownTitle = title === item.title ? null : title
    const ownSub = subline === (item.subline || null) ? null : subline
    if ((pl.title_override || null) !== ownTitle) patch.title_override = ownTitle
    if ((pl.subline_override || null) !== ownSub) patch.subline_override = ownSub
  } else {
    const itemPatch = {}
    if (title !== item.title) itemPatch.title = title
    if (subline !== (item.subline || null)) itemPatch.subline = subline
    if (Object.keys(itemPatch).length) await changed(api, api.from('resource_items').update(itemPatch).eq('id', item.id).select('id'))
    if (pl.title_override) patch.title_override = null
    if (pl.subline_override) patch.subline_override = null
  }

  if (['link', 'text', 'video'].includes(item.kind)) {
    const next = bodyOf(draft)
    const same = body && ['link_url', 'text_body', 'vimeo_url'].every(k => (body[k] ?? null) === (next[k] ?? null))
    if (!same) await api.run(api.from('resource_item_bodies').upsert({ item_id: item.id, ...next }, { onConflict: 'item_id' }).select('item_id'))
  }

  const cols = placementColumns(draft, { toIso, nowIso, newUntil, before: pl })
  // the release is written only when it's changed, so a rule the board can't draw (a named session)
  // stays as it was
  if (releaseUnchanged(draft, pl)) {
    for (const k of ['release_kind', 'release_module_id', 'release_session_id', 'release_at', 'module_id']) delete cols[k]
  }
  for (const [k, v] of Object.entries(cols)) if (k !== 'module_id' && (pl[k] ?? null) === (v ?? null)) delete cols[k]
  if (cols.module_id !== undefined && cols.module_id === pl.module_id) delete cols.module_id
  if (cols.section_id && cols.section_id !== pl.section_id) cols.sort_order = nextOrder(sectionItems)
  Object.assign(patch, cols)
  if (Object.keys(patch).length) {
    await changed(api, api.from('resource_placements').update(patch).eq('id', pl.id).select('id'))
  }

  const add = draft.also.filter(id => !alsoBefore.includes(id))
  const remove = alsoBefore.filter(id => !draft.also.includes(id))
  await showAlsoIn(api, { itemId: item.id, sectionName: others.sectionName, add, remove, actor, at: nowIso })
}

// "Also show it in": the item into another product's section of the same name, else its first
// section, else a new section of that name; opening straight away, which can be changed from that
// product's own view. A placement removed there before is put back instead. Taken out: removed there
// (Put it back in that product brings it back).
export async function showAlsoIn(api, { itemId, sectionName, add, remove, actor, at }) {
  for (const productId of add) {
    const [theirs, sections] = await Promise.all([
      api.run(api.from('resource_placements').select('id, item_id, section_id, sort_order, removed_at').eq('product_id', productId).order('sort_order').order('id')),
      api.run(api.from('resource_sections').select(SECTION_COLUMNS).eq('product_id', productId).order('sort_order').order('id')),
    ])
    const removedHere = theirs.filter(pl => pl.item_id === itemId && pl.removed_at)
      .sort((a, b) => Date.parse(b.removed_at) - Date.parse(a.removed_at))[0]
    if (theirs.some(pl => pl.item_id === itemId && !pl.removed_at)) continue
    if (removedHere && sections.some(s => s.id === removedHere.section_id)) {
      await changed(api, api.from('resource_placements').update({ removed_at: null, removed_by: null }).eq('id', removedHere.id).select('id'))
      continue
    }
    let section = sections.find(s => s.name.toLowerCase() === String(sectionName || '').toLowerCase()) || sortByOrder(sections)[0]
    if (!section) {
      section = (await api.run(api.from('resource_sections')
        .insert({ product_id: productId, name: sectionName, sort_order: 1 }).select(SECTION_COLUMNS)))[0]
    }
    const inSection = theirs.filter(pl => pl.section_id === section.id && !pl.removed_at)
    await api.run(api.from('resource_placements').insert({
      item_id: itemId, product_id: productId, section_id: section.id, sort_order: nextOrder(inSection),
      release_kind: 'now', created_by: actor,
    }).select('id'))
  }
  for (const productId of remove) {
    await api.run(api.from('resource_placements').update({ removed_at: at, removed_by: actor })
      .eq('item_id', itemId).eq('product_id', productId).is('removed_at', null).select('id'))
  }
}
